import { useEffect, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { OptOutMatchType } from '@sdr-agend/admin-schemas'

export interface OptOutKeywordFormValues {
  keyword: string
  matchType: OptOutMatchType
}

interface OptOutKeywordModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (values: OptOutKeywordFormValues) => void | Promise<void>
  submitting?: boolean
  serverError?: string | null
}

const INITIAL: OptOutKeywordFormValues = { keyword: '', matchType: 'literal' }

/**
 * `<OptOutKeywordModal />` — single-purpose creation form (Story 3.9 §AC #5).
 *
 * - Radix Dialog primitive with focus trap + escape close.
 * - Inline regex validation (`new RegExp(value)`) before delegating to the
 *   server. Mirrors the API's 400 INVALID_REGEX so the user gets feedback
 *   instantly even on slow connections.
 * - Resets form state on close to avoid leaking stale input across opens.
 */
export function OptOutKeywordModal({
  open,
  onOpenChange,
  onSubmit,
  submitting = false,
  serverError = null,
}: OptOutKeywordModalProps): React.ReactElement {
  const [values, setValues] = useState<OptOutKeywordFormValues>(INITIAL)
  const [clientError, setClientError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setValues(INITIAL)
      setClientError(null)
    }
  }, [open])

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault()
    setClientError(null)

    const trimmed = values.keyword.trim()
    if (trimmed.length < 2 || trimmed.length > 200) {
      setClientError('Keyword deve ter entre 2 e 200 caracteres.')
      return
    }
    if (values.matchType === 'regex') {
      try {
        // Construct only — discard the instance; it's a syntactic validity check.
        void new RegExp(trimmed)
      } catch (err) {
        setClientError(`Regex inválida: ${(err as Error).message}`)
        return
      }
    }
    void onSubmit({ keyword: trimmed, matchType: values.matchType })
  }

  const errorMessage = clientError ?? serverError

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/60',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
            'rounded-lg border bg-background p-6 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <DialogPrimitive.Title className="text-lg font-semibold">
                Nova Opt-out Keyword
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-muted-foreground">
                Adicione uma palavra ou expressão que dispara opt-out automático.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>

          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="opt-out-keyword">Keyword</Label>
              <Input
                id="opt-out-keyword"
                name="keyword"
                value={values.keyword}
                onChange={(e) => setValues((prev) => ({ ...prev, keyword: e.target.value }))}
                placeholder="ex: cancelar, parar, \\bstop\\b"
                minLength={2}
                maxLength={200}
                required
                autoFocus
              />
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Tipo de match</legend>
              <div className="flex gap-4" role="radiogroup" aria-label="Tipo de match">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="matchType"
                    value="literal"
                    checked={values.matchType === 'literal'}
                    onChange={() => setValues((prev) => ({ ...prev, matchType: 'literal' }))}
                    className="h-4 w-4"
                  />
                  Literal
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="matchType"
                    value="regex"
                    checked={values.matchType === 'regex'}
                    onChange={() => setValues((prev) => ({ ...prev, matchType: 'regex' }))}
                    className="h-4 w-4"
                  />
                  Regex
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Match é case-insensitive. Para regex, use a sintaxe JavaScript (ex: <code>\\bstop\\b</code>).
              </p>
            </fieldset>

            {errorMessage !== null && (
              <p
                role="alert"
                className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {errorMessage}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <DialogPrimitive.Close asChild>
                <Button type="button" variant="outline" disabled={submitting}>
                  Cancelar
                </Button>
              </DialogPrimitive.Close>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Salvando…' : 'Criar keyword'}
              </Button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default OptOutKeywordModal
